import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/pagination";
import "swiper/swiper-bundle.css";
import AnnualPassCard, { formatMainProductPrice } from "@/components/Package/AnnualCard";
import { cn } from "@/lib/utils";
import { Tag } from "@bookips/sds";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SwiperCore, { A11y, Autoplay, EffectCoverflow, Grid, Navigation, Pagination, Scrollbar } from "swiper";
const AnnualPassSwiper = (param)=>{
    let { passItems , handleClick , tabName  } = param;
    SwiperCore.use([
        Navigation,
        Pagination,
        Scrollbar,
        Autoplay,
        A11y,
        Grid,
        EffectCoverflow
    ]);
    const swiperRef = useRef(null);
    const paginationRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(null);
    // 칩이 3개 이하면 폭을 채우고(grow), 초과면 고정 폭 + 가로 스크롤
    const shouldFillPagination = passItems.length <= 3;
    const handleSlideChange = useCallback((index)=>{
        if (swiperRef.current && swiperRef.current.slideTo) {
            swiperRef.current.slideTo(index);
            setCurrentIndex(index);
        }
    }, []);
    const itemsKey = passItems.map((item)=>item.id).join("|");
    const initialSlide = useMemo(()=>{
        if (!passItems) return null;
        const isBadge = passItems.findIndex((item)=>item.badgeTitle);
        return isBadge > 0 ? isBadge : 0;
    }, [
        passItems
    ]);
    // 최초 마운트 시 Swiper 렌더 게이트(currentIndex !== null)를 열고, 목록이 바뀔 때 위치를 재조정한다.
    // deps에 passItems 대신 itemsKey를 쓰는 이유: react-query refetch처럼 내용은 같고 참조만 새로 온
    // 경우에 유저가 스와이프해 둔 위치가 되감기지 않게 하려고. initialSlide는 아이템 수가 줄어드는 경우를
    // 못 잡아(뱃지 없는 목록끼리는 값이 계속 0) 범위 초과를 막으려면 itemsKey가 함께 필요하다.
    useEffect(()=>{
        if (typeof initialSlide === "number") {
            setCurrentIndex(initialSlide);
        }
    }, [
        itemsKey,
        initialSlide
    ]);
    useEffect(()=>{
        // 칩 클릭·초기값처럼 현재 슬라이드와 다를 때만 이동
        // (스와이프 중에는 activeIndex === currentIndex라 재호출되지 않아 overshoot 방지)
        // 알려진 이슈: 스와이프해 둔 상태에서 학년 탭을 바꾸면 Swiper가 인덱스를 클램프한 카드를 먼저 그린 뒤
        // 여기서 초기 슬라이드로 300ms 애니메이션으로 되돌아간다. slideTo(currentIndex, 0)으로 컷 처리하면
        // 해소되지만 아래 칩 스크롤은 smooth라 둘의 속도가 어긋난다 — 연출은 함께 봐야 해서 미뤄둔 상태.
        if (swiperRef.current && currentIndex !== null && swiperRef.current.activeIndex !== currentIndex) {
            swiperRef.current.slideTo(currentIndex);
        }
        // 활성 칩이 스크롤 영역 밖이면 중앙으로 가로 스크롤
        // NOTE: Swiper의 Thumbs 모듈로 처리하는 것이 정석이나, 커스텀 칩(제목+가격+배지)을
        //       썸네일 Swiper로 옮기면 코드량·배지 잘림 등 비용이 커서 개편 전까지 ref 방식으로 임시 처리.
        //       추후 개편 시 Thumbs 모듈 전환 검토.
        const container = paginationRef.current;
        if (container && currentIndex !== null) {
            const active = container.children[currentIndex];
            if (active) {
                const containerRect = container.getBoundingClientRect();
                const activeRect = active.getBoundingClientRect();
                const delta = activeRect.left - containerRect.left - (container.clientWidth - active.clientWidth) / 2;
                container.scrollTo({
                    left: container.scrollLeft + delta,
                    behavior: "smooth"
                });
            }
        }
    }, [
        currentIndex
    ]);
    return(// 슬라이드 자체의 display/height/폭 보정은 globals.css의 .pass-main-swiper .swiper-slide 규칙이 담당한다.
    // (레이어 없이 로드되는 swiper-bundle.css를 Tailwind 유틸리티로는 덮을 수 없어서)
    // 비활성 슬라이드 축소(scale)는 두지 않는다 — 좌우 여백(20px)에서 카드 간격(12px)을 뺀
    // 8px peek을 카드 중심 기준으로 잡아먹어 앞뒤 카드가 화면 밖으로 밀려난다.
    /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mb-2",
                children: /*#__PURE__*/ _jsx("ul", {
                    ref: paginationRef,
                    className: "scrollbar-hide flex w-full gap-2 overflow-x-auto px-5",
                    children: passItems.map((pass, index)=>/*#__PURE__*/ _jsxs("li", {
                            role: "button",
                            onClick: ()=>{
                                handleSlideChange(index);
                            },
                            className: cn("relative flex shrink-0 basis-[calc((100%-16px)/3)] flex-col items-center justify-between gap-1 rounded-lg border p-3 text-center", shouldFillPagination ? "grow" : "grow-0", currentIndex === index ? "border-gray-700 bg-white" : "border-gray-100"),
                            children: [
                                (pass.badgeTitle || pass.tagTitle) && /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap justify-center gap-1",
                                    children: [
                                        pass.badgeTitle && /*#__PURE__*/ _jsx(Tag, {
                                            accent: true,
                                            color: "red",
                                            size: "sm",
                                            bold: true,
                                            className: "whitespace-nowrap",
                                            children: pass.badgeTitle
                                        }),
                                        pass.tagTitle && /*#__PURE__*/ _jsx(Tag, {
                                            color: "gray",
                                            size: "sm",
                                            bold: true,
                                            className: "whitespace-nowrap",
                                            children: pass.tagTitle
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: cn("typo-subtitle2", currentIndex === index ? "text-gray-900" : "text-secondary"),
                                    children: pass.title
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: cn("typo-subtitle1 whitespace-nowrap", currentIndex === index ? "text-primary-main" : "text-secondary"),
                                    children: formatMainProductPrice(pass)
                                })
                            ]
                        }, pass.id))
                })
            }),
            currentIndex !== null && /*#__PURE__*/ _jsx(Swiper, {
                onSwiper: (swiper)=>swiperRef.current = swiper,
                height: 300,
                // 시작 위치를 넘기지 않으면 0번에서 시작해 뱃지 카드까지 애니메이션으로 흘러간다
                initialSlide: initialSlide !== null && initialSlide !== void 0 ? initialSlide : 0,
                slidesPerView: "auto",
                centeredSlides: true,
                spaceBetween: 12,
                modules: [
                    Pagination,
                    Navigation
                ],
                className: "pass-main-swiper",
                onSlideChange: (swiper)=>{
                    // 스와이프 경로에서는 상태만 갱신 (slideTo 재호출 금지 → 손 조작과 충돌 방지)
                    if (swiper && swiper.activeIndex !== undefined) {
                        setCurrentIndex(swiper.activeIndex);
                    }
                },
                children: passItems.map((item)=>/*#__PURE__*/ _jsx(SwiperSlide, {
                        children: /*#__PURE__*/ _jsx(AnnualPassCard, {
                            ...item,
                            handleClick: handleClick,
                            tabName: tabName
                        })
                    }, item.id))
            })
        ]
    }));
};
export default AnnualPassSwiper;
